import { z } from "zod";
import { CreateDemoAppointmentRequestSchema, CreateDemoPublicBookingRequestSchema, DemoAppointmentPatientSchema, DemoAppointmentSchema, DemoAppointmentStatusSchema, DemoAvailabilityResponseSchema, DemoAvailabilitySlotSchema, DemoPublicSchedulingConfigurationSchema, DemoScheduledIntakeStatusSchema, DemoScheduleProviderSchema, DemoSchedulingConfigurationSchema, DemoSchedulingHoursSchema, DemoSchedulingStateSchema, PatientCancellationReasonSchema, ProviderAvailabilitySchema, refineAvailabilityDateRange, refineIntakeReviewOpen, SCHEDULING_LIMITS, SchedulingIntakeReviewSummarySchema, SchedulingIntakeSectionReviewSchema, SchedulingSettingsSchema, UpdateDemoAppointmentRequestSchema, WebsiteOriginsSchema, } from "./demo-scheduling.js";
import { ConsentDocumentTitleSchema, ConsentDocumentVersionNumberSchema, PatientConsentChannelSchema, PatientConsentDecisionSchema, PatientConsentKindSchema, PublicBookingConsentDocumentsSchema, PublicBookingConsentSchema, PublicConsentRequirementSchema, PublicIntakeConsentSchema, } from "./patient-consent.js";
import { PatientMessageChannelSchema, PatientMessageKindSchema, PatientMessageStatusSchema, } from "./patient-messaging.js";
import { PatientMessagingConsentSchema, StaffMessagingDecisionInputSchema, } from "./patient-messaging-consent.js";
import { PatientAdministrativeSexSchema } from "./patient-profile.js";
import { FULL_PATIENT_INTAKE_SECTIONS, HealthCardFollowUpSchema, OntarioHealthNumberSchema, PATIENT_INTAKE_SCHEMA_VERSION, PatientIntakeAnswersSchema, PatientIntakeSectionsSchema, PatientQuestionnaireCoverageSchema, PatientQuestionnaireIdentitySchema, PatientQuestionnaireSchema, } from "./patient-questionnaire.js";
import { IanaTimeZoneSchema, IdempotencyKeySchema, IsoDateTimeSchema } from "./primitives.js";
const ProviderIdSchema = DemoScheduleProviderSchema.shape.id;
export const ClinicSlugSchema = z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(80);
// Canonical base64url encoding of exactly 32 bytes; the final character has two zero bits.
export const PublicAppointmentTokenSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{42}[AEIMQUYcgkosw048]$/);
export const PublicAppointmentTokenPurposeSchema = z.enum(["intake", "manage", "document"]);
/**
 * How the patient lane names a physician: a random handle, unique within the clinic and never
 * the physician's account id ("pv_" and 24 lowercase hex digits). Staff routes use real ids.
 */
export const PublicProviderHandleSchema = z.string().regex(/^pv_[0-9a-f]{24}$/);
export const PublicBookingConfigurationSchema = DemoPublicSchedulingConfigurationSchema.extend({
    clinicSlug: ClinicSlugSchema,
    turnstileSiteKey: z.string().min(1).max(200),
    providers: z
        .array(DemoPublicSchedulingConfigurationSchema.shape.providers.element.extend({
        id: PublicProviderHandleSchema,
    }))
        .max(SCHEDULING_LIMITS.providers),
    /** What the booking review asks the patient to agree to: current versions, shown in full. */
    consentDocuments: PublicBookingConsentDocumentsSchema,
    /**
     * PA-12. The clinic's weekly hours for the booking page; never a physician's hours or
     * closures. Absent from an API older than PA-12 (the page then shows no hours).
     */
    clinicHours: z.array(DemoSchedulingHoursSchema).max(7).optional(),
    /**
     * PA-12. The websites whose pages may show the widget's form. Absent from an API older than
     * PA-12, which the page reads as none, so a framed page shows only a link to the booking page.
     */
    websiteOrigins: WebsiteOriginsSchema.optional(),
});
/**
 * A patient's availability search. It is a POST body so an existing patient's verification
 * pass never enters a URL.
 */
export const PublicAvailabilityRequestSchema = z
    .object({
    fromDate: z.iso.date(),
    toDate: z.iso.date(),
    appointmentTypeId: DemoAvailabilitySlotSchema.shape.appointmentTypeId,
    /** Only for visit types where the patient chooses the physician. */
    providerId: PublicProviderHandleSchema.optional(),
    /** Required while the clinic books existing patients only. */
    verification: PublicAppointmentTokenSchema.optional(),
})
    .strict()
    .superRefine(refineAvailabilityDateRange);
/**
 * A bookable time. Without a providerId the clinic assigns the physician when the patient books
 * (one slot per start time, at the visit type's own duration).
 */
export const PublicAvailabilitySlotSchema = DemoAvailabilitySlotSchema.extend({
    providerId: PublicProviderHandleSchema.optional(),
});
export const PublicAvailabilityResponseSchema = DemoAvailabilityResponseSchema.extend({
    slots: z.array(PublicAvailabilitySlotSchema).max(10_000),
});
export const CreatePublicBookingRequestSchema = CreateDemoPublicBookingRequestSchema.extend({
    /** Omitted when the clinic assigns the physician. */
    providerId: PublicProviderHandleSchema.optional(),
    /** The existing patient's pass: one booking each. Not part of the idempotent replay. */
    verification: PublicAppointmentTokenSchema.optional(),
    turnstileToken: z.string().min(1).max(2048),
    /** The ticked terms box (and messaging line, when offered), naming the versions shown. */
    consent: PublicBookingConsentSchema,
    /**
     * The patient's Ontario health card number (PA-10 / EXECDEV-217), as its 10 digits: optional,
     * and only on a returning patient's booking at a clinic that books anyone, to help staff find
     * the chart. Never required. Never on a new patient's booking, nor on an existing patient's
     * verified booking, whose pass already matched a card. The store uses it inside the booking
     * transaction to find charts and keeps only which charts matched and why: never the number or
     * anything derived from it. It is never echoed or logged and, like the challenge and the pass,
     * not part of the idempotent replay. The receipt is the same whether it matched a chart, matched
     * none, or was not given.
     */
    healthNumber: OntarioHealthNumberSchema.optional(),
}).superRefine((value, context) => {
    if (value.healthNumber === undefined)
        return;
    if (value.patient.kind !== "returning") {
        context.addIssue({
            code: "custom",
            message: "Only a returning patient's booking takes a health card number",
            path: ["healthNumber"],
        });
    }
    if (value.verification !== undefined) {
        context.addIssue({
            code: "custom",
            message: "A verified booking already matched a health card",
            path: ["healthNumber"],
        });
    }
});
/**
 * What a booking returns. It never says whether, or how, the patient matched a chart (PA-10): the
 * same fields whether a health card was given, matched one chart, several or none.
 */
export const PublicBookingReceiptSchema = z
    .object({
    created: z.boolean(),
    bookingReference: z.string().uuid(),
    /** Absent when the clinic assigned the physician. */
    providerId: PublicProviderHandleSchema.optional(),
    appointmentTypeId: z.string().min(1).max(100),
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
    status: DemoAppointmentStatusSchema,
    intakeStatus: DemoScheduledIntakeStatusSchema,
    notice: z.string().max(500),
})
    .strict();
/** A questionnaire link's path: the clinic slug, and the token only in the fragment. */
export const PublicIntakeQuestionnaireUrlSchema = z
    .string()
    .regex(/^\/intake\/[a-z0-9-]+#token=[A-Za-z0-9_-]{43}$/);
/** A manage link's path: the clinic slug, and the token only in the fragment. */
export const PublicManageAppointmentUrlSchema = z
    .string()
    .regex(/^\/manage\/[a-z0-9-]+#token=[A-Za-z0-9_-]{43}$/);
/** A document link's token stays in the fragment, never a request path or query. */
export const PublicDocumentLinkUrlSchema = z
    .string()
    .regex(/^\/document\/[a-z0-9-]+#token=[A-Za-z0-9_-]{43}$/);
/**
 * A patient link's path on the patient app: the questionnaire or manage page of the clinic, with
 * the token only in the fragment (PublicIntakeQuestionnaireUrlSchema,
 * PublicManageAppointmentUrlSchema). A message's link (PA-7) is the patient app's origin plus
 * this. Throws on a slug or token those schemas refuse, so a malformed link is never sent.
 */
export function publicPatientLinkPath(purpose, clinicSlug, token) {
    const path = `/${purpose}/${clinicSlug}#token=${token}`;
    if (purpose === "document")
        return PublicDocumentLinkUrlSchema.parse(path);
    return purpose === "intake"
        ? PublicIntakeQuestionnaireUrlSchema.parse(path)
        : PublicManageAppointmentUrlSchema.parse(path);
}
export const PublicBookingResponseSchema = PublicBookingReceiptSchema.extend({
    intakeQuestionnaireUrl: PublicIntakeQuestionnaireUrlSchema.optional(),
    manageAppointmentUrl: PublicManageAppointmentUrlSchema.optional(),
});
/**
 * An existing-patients-only clinic asks the patient to match one of its active charts by health
 * card number and date of birth before showing any times.
 */
export const VerifyPublicBookingPatientRequestSchema = z
    .object({
    healthNumber: OntarioHealthNumberSchema,
    dateOfBirth: z.iso.date(),
    turnstileToken: z.string().min(1).max(2048),
})
    .strict();
/**
 * Every failure has the same body whatever the cause; locked means the attempts are used up and
 * the patient should call the clinic. A success returns the pass once: it lasts 20 minutes,
 * books once, and travels only in POST bodies. firstName (the chart's legal first name) is
 * given only when exactly one chart matched.
 */
export const VerifyPublicBookingPatientResponseSchema = z.discriminatedUnion("verified", [
    z
        .object({
        verified: z.literal(true),
        verification: PublicAppointmentTokenSchema,
        firstName: z.string().trim().min(1).max(100).optional(),
        expiresAt: IsoDateTimeSchema,
    })
        .strict(),
    z.object({ verified: z.literal(false), locked: z.boolean() }).strict(),
]);
/**
 * Sex, mobile number and email as the patient typed them when booking online. Sex "unknown"
 * (prefer not to say) is never sent: the questionnaire asks again rather than lock it.
 */
export const PublicBookingIdentitySchema = z
    .object({
    administrativeSex: PatientAdministrativeSexSchema.exclude(["unknown"]).optional(),
    phone: z.string().trim().min(1).max(60).optional(),
    email: z.string().trim().email().max(254).optional(),
})
    .strict();
/**
 * The booking values the questionnaire shows read-only: only those its own fields accept, so
 * a booking phone number over the questionnaire's 40 characters stays editable, and never sex
 * "unknown". Everything else stays an ordinary answer.
 */
export function lockedBookingIdentity(identity) {
    const locked = {};
    if (identity === undefined)
        return locked;
    const fields = PatientQuestionnaireIdentitySchema.shape;
    const sex = fields.administrativeSex.safeParse(identity.administrativeSex);
    if (sex.success && sex.data !== "unknown")
        locked.administrativeSex = sex.data;
    const phone = fields.phone.safeParse(identity.phone);
    if (identity.phone !== undefined && phone.success)
        locked.phone = phone.data;
    const email = fields.email.safeParse(identity.email);
    if (email.success && email.data !== undefined)
        locked.email = email.data;
    return locked;
}
/**
 * Wrong dates of birth one patient link takes before it is revoked (owner decision O2, PA-7), so
 * a link that leaked from a text or an email cannot be guessed open; the link then answers like
 * any unusable link and the patient calls the clinic. This limit covers intake/manage links;
 * document links use their separate atomic three-attempt limit.
 */
export const PUBLIC_LINK_DATE_OF_BIRTH_ATTEMPT_LIMIT = 5;
export const OpenPublicAppointmentTokenRequestSchema = z
    .object({
    token: PublicAppointmentTokenSchema,
    purpose: PublicAppointmentTokenPurposeSchema,
    dateOfBirth: z.iso.date(),
})
    .strict();
/**
 * What a patient link belongs to: a booked appointment, or a walk-in visit already in the
 * clinic that the desk sent a questionnaire link to (PA-5).
 */
export const PublicVisitKindSchema = z.enum(["appointment", "walk_in"]);
/**
 * Whether the patient may cancel or change the time from their manage link now (PA-9), on the
 * store's clock:
 *
 * - available: offered.
 * - past_cutoff: inside the clinic's cancellation cutoff, until the visit starts.
 * - contact_clinic (change time only): the clinic takes time changes by phone
 *   (patientRescheduling contact_clinic), or it no longer books online (online booking off, or
 *   walk-in only).
 * - not_eligible (change time only): the booking could not be made online today: its visit type
 *   is no longer active and bookable online; or it is a new patient's and new patients are no
 *   longer booked online (the clinic's or the visit type's rule, including a clinic that now
 *   books existing patients only and a booking with no chart). A returning patient's booking may
 *   still move: it passed the existing-patient check when it was made.
 *
 * Every state but available is a rule the page names, with the clinic's phone
 * (PublicAppointmentSession.clinicPhone) and no button for it.
 */
export const PublicSelfServiceStateSchema = z.enum([
    "available",
    "past_cutoff",
    "contact_clinic",
    "not_eligible",
]);
/**
 * What the patient may do from their manage link now (PA-9, lead decisions L2 and L4). The store
 * works it out when the link is opened and decides every request again, so a page left open past
 * changeBy is refused with PUBLIC_CHANGE_CUTOFF_PASSED_ERROR_CODE.
 *
 * - Confirm is offered while the booking is booked: a manage link opens only until the visit
 *   starts, so it never needs a state. Confirming keeps the link, and a booking already confirmed
 *   stays as it is.
 * - cancel and reschedule: while now is before changeBy (the start minus the clinic's
 *   cancellation cutoff; the start itself when the cutoff is 0); reschedule also needs the rules
 *   above. Either one uses the link up.
 */
export const PublicAppointmentSelfServiceSchema = z
    .object({
    cancel: PublicSelfServiceStateSchema.extract(["available", "past_cutoff"]),
    reschedule: PublicSelfServiceStateSchema,
    changeBy: IsoDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if ((value.cancel === "past_cutoff") !== (value.reschedule === "past_cutoff")) {
        context.addIssue({
            code: "custom",
            message: "The cutoff closes cancelling and changing the time together",
            path: ["reschedule"],
        });
    }
});
/**
 * The public lane's error code (HTTP 409) when a patient's cancel or time change comes inside the
 * clinic's cancellation cutoff (PA-9), as from a page left open past changeBy. Nothing changed
 * and the link still works; the page reopens the session, which names the rule. Given only after
 * the date of birth matched. From the start the link no longer opens at all (the generic
 * "unavailable").
 */
export const PUBLIC_CHANGE_CUTOFF_PASSED_ERROR_CODE = "CHANGE_CUTOFF_PASSED";
export const PublicAppointmentSessionSchema = z
    .object({
    bookingReference: z.string().uuid(),
    version: z.number().int().positive(),
    clinicName: z.string().min(1).max(240),
    clinicAddress: z.string().max(240),
    providerDisplayName: z.string().max(240),
    appointmentTypeName: z.string().max(240),
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
    status: DemoAppointmentStatusSchema,
    intakeStatus: DemoScheduledIntakeStatusSchema,
    purpose: PublicAppointmentTokenPurposeSchema,
    notice: z.string().max(500),
    /**
     * What the questionnaire still asks the patient to agree to: the terms only when no
     * agreement is recorded for this appointment yet (it was not booked online), the messaging
     * line only when offered and not yet answered. Both null on a manage link.
     */
    consentDocuments: PublicConsentRequirementSchema,
    /**
     * Only on an intake link of an online booking: what the patient typed when booking, which
     * the questionnaire shows read-only (lockedBookingIdentity). Absent on manage links and on
     * bookings staff entered, whose values the patient never typed (PA-6).
     */
    bookingIdentity: PublicBookingIdentitySchema.optional(),
    /**
     * A walk-in's link (PA-5) says walk_in: the patient is already at the clinic, so the page
     * shows no appointment time or physician (startAt is the arrival; providerDisplayName and
     * appointmentTypeName are the store's fixed walk-in labels). Absent on every other link, so
     * a booking's session is unchanged for a patient app from before PA-5; read it with
     * publicSessionVisitKind.
     */
    visitKind: PublicVisitKindSchema.optional(),
    /**
     * The parts of the questionnaire the link asks for, in form order, when that is not every
     * section: About you is left out only for a walk-in whose chart existed before this visit,
     * since the chart already says who they are. Read it with publicSessionQuestionnaireSections.
     * Never on a manage link.
     */
    questionnaireSections: PatientIntakeSectionsSchema.optional(),
    /**
     * PA-9, a booking's manage link only (agent-api strips them from every other session), and
     * absent from a server before PA-9, whose page offers the old confirm and cancel only:
     *
     * - timeZone: the clinic's, so the page shows the visit on the clinic's clock.
     * - clinicPhone: the site phone for "call the clinic", when one is on file (the public
     *   configuration's rule); the page says "call the clinic" without one.
     * - providerId: the physician's handle when the patient chose the physician; absent when the
     *   clinic assigned one. A new time may name another physician's handle only then.
     * - selfService: what the patient may do now.
     */
    timeZone: IanaTimeZoneSchema.optional(),
    clinicPhone: DemoPublicSchedulingConfigurationSchema.shape.clinicPhone,
    providerId: PublicProviderHandleSchema.optional(),
    selfService: PublicAppointmentSelfServiceSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.selfService !== undefined &&
        Date.parse(value.selfService.changeBy) > Date.parse(value.startAt)) {
        context.addIssue({
            code: "custom",
            message: "Online changes close by the start",
            path: ["selfService", "changeBy"],
        });
    }
});
/** The session's visit kind: an appointment unless the link says walk_in. */
export function publicSessionVisitKind(session) {
    return session.visitKind ?? "appointment";
}
/** The sections the session's questionnaire asks: every one unless the link says otherwise. */
export function publicSessionQuestionnaireSections(session) {
    return session.questionnaireSections ?? FULL_PATIENT_INTAKE_SECTIONS;
}
/**
 * `POST /v1/public/:clinicSlug/access/open` on an intake link whose questionnaire was already
 * submitted, with the right date of birth (PA-5, lead decision H2): the patient is told it was
 * sent and is never shown the answers, the terms or a form. Any other used, revoked or expired
 * link, and any wrong date of birth, keeps the one generic "unavailable" refusal.
 */
export const PublicIntakeAlreadySubmittedSchema = z
    .object({
    alreadySubmitted: z.literal(true),
    purpose: z.literal("intake"),
    clinicName: z.string().min(1).max(240),
    visitKind: PublicVisitKindSchema,
})
    .strict();
/** What opening a patient link returns: the session, or an intake already submitted. */
export const PublicAppointmentOpenResponseSchema = z.union([
    PublicAppointmentSessionSchema,
    PublicIntakeAlreadySubmittedSchema,
]);
export function isPublicIntakeAlreadySubmitted(response) {
    return Object.hasOwn(response, "alreadySubmitted");
}
/**
 * The public lane's error code (HTTP 409) when an intake link's questionnaire was already
 * submitted and this request is not an exact retry of that submit: answers from a second
 * device, or changed answers. Nothing from this request was saved. Given only after the date of
 * birth matched; a wrong date of birth keeps the generic "unavailable" refusal.
 */
export const PUBLIC_INTAKE_ALREADY_SUBMITTED_ERROR_CODE = "INTAKE_ALREADY_SUBMITTED";
/**
 * `POST /v1/public/:clinicSlug/intake/complete`. The idempotencyKey names one submit: an exact
 * retry of a submit that succeeded (same link, date of birth, key and body, as when a response
 * was lost) returns that submit's stored PublicIntakeReceipt and saves nothing new. Anything else
 * on a used link is refused with PUBLIC_INTAKE_ALREADY_SUBMITTED_ERROR_CODE.
 */
export const CompletePublicIntakeRequestSchema = z
    .object({
    token: PublicAppointmentTokenSchema,
    dateOfBirth: z.iso.date(),
    idempotencyKey: IdempotencyKeySchema,
    /** Without About you exactly when the session's questionnaireSections leave it out. */
    intake: PatientIntakeAnswersSchema,
    /** Present when the session's consentDocuments asked for something. */
    consent: PublicIntakeConsentSchema.optional(),
})
    .strict();
/**
 * `POST /v1/public/:clinicSlug/appointments/manage`: confirm or cancel from a manage link (a new
 * time is PublicRescheduleRequestSchema, PA-9). Confirm keeps the link working, and confirming a
 * booking already confirmed changes nothing; cancel uses the link up, and a used link answers like
 * any unusable link. cancellationReason goes only with cancel: the patient page asks for one of
 * the fixed reasons, and a client from before PA-9 sends none.
 */
export const ManagePublicAppointmentRequestSchema = z
    .object({
    token: PublicAppointmentTokenSchema,
    dateOfBirth: z.iso.date(),
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: z.number().int().positive(),
    action: z.enum(["cancel", "confirm"]),
    cancellationReason: PatientCancellationReasonSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.cancellationReason !== undefined && value.action !== "cancel") {
        context.addIssue({
            code: "custom",
            message: "Only a cancellation has a reason",
            path: ["cancellationReason"],
        });
    }
});
export const PublicAppointmentMutationReceiptSchema = z
    .object({
    bookingReference: z.string().uuid(),
    status: DemoAppointmentStatusSchema,
    intakeStatus: DemoScheduledIntakeStatusSchema,
    notice: z.string().max(500),
})
    .strict();
/**
 * `POST /v1/public/:clinicSlug/appointments/manage/availability` (PA-9): the times the booking
 * can move to, asked with the manage link in the body (never in a URL). The link is not used up
 * and no verification pass is needed: the link and date of birth are the authority. Times are
 * the booking's own visit type, by the booking rules (minimum lead, and at least the cancellation
 * cutoff ahead), leaving out the booking's own time. providerId (a handle) narrows to one
 * physician, only when the patient chose the physician; a clinic-assigned booking's times name
 * none.
 */
export const PublicManageAvailabilityRequestSchema = z
    .object({
    token: PublicAppointmentTokenSchema,
    dateOfBirth: z.iso.date(),
    fromDate: z.iso.date(),
    toDate: z.iso.date(),
    providerId: PublicProviderHandleSchema.optional(),
})
    .strict()
    .superRefine(refineAvailabilityDateRange);
/** A physician the listed times name: the handle, never the account id. */
export const PublicManageAvailabilityProviderSchema = z
    .object({
    id: PublicProviderHandleSchema,
    displayName: DemoScheduleProviderSchema.shape.displayName,
    credentials: DemoScheduleProviderSchema.shape.credentials,
})
    .strict();
/** The times, and the physicians they name (none for a clinic-assigned booking). */
export const PublicManageAvailabilityResponseSchema = PublicAvailabilityResponseSchema.extend({
    providers: z.array(PublicManageAvailabilityProviderSchema).max(SCHEDULING_LIMITS.providers),
}).superRefine((value, context) => {
    const ids = new Set(value.providers.map((provider) => provider.id));
    if (ids.size !== value.providers.length) {
        context.addIssue({
            code: "custom",
            message: "Each physician is listed once",
            path: ["providers"],
        });
    }
    value.slots.forEach((slot, index) => {
        if (slot.providerId !== undefined && !ids.has(slot.providerId)) {
            context.addIssue({
                code: "custom",
                message: "A time names a listed physician",
                path: ["slots", index, "providerId"],
            });
        }
    });
});
/**
 * `POST /v1/public/:clinicSlug/appointments/reschedule` (PA-9): move the booking to a time from
 * PublicManageAvailabilityResponseSchema. Same visit type. providerId (a handle, the time's own)
 * only when the patient chose the physician: it may name another physician who takes the visit
 * type, and without it the physician stays. A clinic-assigned booking names none: it keeps its
 * physician when free at the new time, otherwise the clinic assigns one (still clinic-assigned).
 * The
 * move uses the link up and replaces the booking's links: the response carries the new ones,
 * once. There is no replay of a lost response (lead decision L5): the page says "If this page
 * didn't update, call the clinic", and a change-of-time message, when the clinic sends them,
 * carries a fresh link.
 */
export const PublicRescheduleRequestSchema = z
    .object({
    token: PublicAppointmentTokenSchema,
    dateOfBirth: z.iso.date(),
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: z.number().int().positive(),
    startAt: IsoDateTimeSchema,
    providerId: PublicProviderHandleSchema.optional(),
})
    .strict();
/**
 * What the store returns for a move: the receipt with the new time, and the booking's new version
 * (a calendar file's SEQUENCE, lead decision L9).
 */
export const PublicRescheduleReceiptSchema = PublicAppointmentMutationReceiptSchema.extend({
    version: z.number().int().positive(),
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
});
/**
 * The move's response: the receipt plus the new links, returned once. The questionnaire link is
 * present while the questionnaire is pending (intakeStatus required or sent), as for a staff
 * re-issue.
 */
export const PublicRescheduleResponseSchema = PublicRescheduleReceiptSchema.extend({
    manageAppointmentUrl: PublicManageAppointmentUrlSchema,
    intakeQuestionnaireUrl: PublicIntakeQuestionnaireUrlSchema.optional(),
});
/**
 * What a questionnaire submit returns. An exact retry gets the stored receipt of the submit it
 * repeats, unchanged, so a patient whose response was lost sees the same confirmation.
 */
export const PublicIntakeReceiptSchema = PublicAppointmentMutationReceiptSchema;
/**
 * Who the patient said they are and how they are covered: the administrative section, which
 * any scheduling role reviews and applies to the chart (by creating it, or as demographics).
 */
export const SchedulingIntakeAdministrativeAnswersSchema = z
    .object({
    /**
     * Null when the questionnaire did not ask (a schema version after 1: a returning patient's
     * walk-in skips About you). A chart is never created, and no identity field applied, from
     * such answers (detail intake_identity_missing).
     */
    identity: PatientQuestionnaireIdentitySchema.strict().nullable(),
    coverage: PatientQuestionnaireCoverageSchema,
})
    .strict();
/**
 * The patient's reason for the visit, which every role reads. Before arrival staff may make it
 * the booking reason that check-in copies to the chief complaint; it is not applied after.
 */
export const SchedulingIntakeVisitAnswersSchema = z
    .object({ reason: PatientQuestionnaireSchema.shape.reason })
    .strict();
/**
 * The health answers: the clinical section, for physicians and nurses only. Pregnancy and
 * symptoms are shown here and written nowhere (not the chart, not the visit context).
 */
export const SchedulingIntakeClinicalAnswersSchema = z
    .object({
    symptoms: PatientQuestionnaireSchema.shape.symptoms,
    allergies: PatientQuestionnaireSchema.shape.allergies,
    medications: PatientQuestionnaireSchema.shape.medications,
    medicalHistory: PatientQuestionnaireSchema.shape.medicalHistory,
    pregnant: PatientQuestionnaireSchema.shape.pregnant,
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.symptoms).size !== value.symptoms.length) {
        context.addIssue({
            code: "custom",
            message: "Each symptom can be listed only once",
            path: ["symptoms"],
        });
    }
});
/** What staff did with the answers: each apply is recorded once, with who and when. */
export const SchedulingIntakeApplicationKindSchema = z.enum([
    /** An existing chart was linked to the booking. */
    "chart_linked",
    /** A new chart was registered from the answers and linked. */
    "chart_created",
    /** Staff confirmed none of the probable charts is this patient. */
    "chart_mismatch",
    /** Identity or coverage answers were applied to the linked chart. */
    "demographics",
    /** The patient's reason became the booking reason before arrival. */
    "reason",
    "allergies",
    "medications",
    /** Medical history, applied as past medical history. */
    "pmh",
]);
/** The section each kind of application belongs to. */
export const SCHEDULING_INTAKE_APPLICATION_SECTION = {
    chart_linked: "administrative",
    chart_created: "administrative",
    chart_mismatch: "administrative",
    demographics: "administrative",
    reason: "administrative",
    allergies: "clinical",
    medications: "clinical",
    pmh: "clinical",
};
export const SchedulingIntakeApplicationSchema = z
    .object({
    id: z.string().uuid(),
    kind: SchedulingIntakeApplicationKindSchema,
    appliedAt: IsoDateTimeSchema,
    /** The staff member's display name, or "A staff member"; never an email address. */
    appliedByName: z.string().trim().min(1).max(200),
    /** Fields changed or entries added; 1 for a chart link, creation, mismatch or reason. */
    itemCount: z.number().int().min(0).max(200),
})
    .strict();
/**
 * A submitted questionnaire, split into the sections staff review. The clinical section, and
 * applications of its kinds, are left out for front desk and clinic admins.
 */
export const SchedulingIntakeSubmissionSchema = z
    .object({
    id: z.string().uuid(),
    submittedAt: IsoDateTimeSchema,
    /**
     * 1 is the full PA-2 questionnaire (PatientQuestionnaireSchema), which always has identity.
     * Later versions may leave sections out; a client shows the sections it receives.
     */
    schemaVersion: z.number().int().min(1).max(100),
    /** Whether answers may still be applied and sections marked reviewed (see the summary). */
    open: z.boolean(),
    visit: SchedulingIntakeVisitAnswersSchema,
    administrative: z
        .object({
        answers: SchedulingIntakeAdministrativeAnswersSchema,
        review: SchedulingIntakeSectionReviewSchema,
    })
        .strict(),
    clinical: z
        .object({
        answers: SchedulingIntakeClinicalAnswersSchema,
        review: SchedulingIntakeSectionReviewSchema,
    })
        .strict()
        .optional(),
    /** Oldest first. */
    applications: z.array(SchedulingIntakeApplicationSchema).max(200),
})
    .strict()
    .superRefine((value, context) => {
    refineIntakeReviewOpen(value.open, { administrative: value.administrative.review.state, clinical: value.clinical?.review.state }, context, (section) => [section, "review", "state"]);
    if (value.schemaVersion === PATIENT_INTAKE_SCHEMA_VERSION.full &&
        value.administrative.answers.identity === null) {
        context.addIssue({
            code: "custom",
            message: "A version 1 questionnaire always has identity answers",
            path: ["administrative", "answers", "identity"],
        });
    }
    if (value.schemaVersion === PATIENT_INTAKE_SCHEMA_VERSION.withoutIdentity &&
        value.administrative.answers.identity !== null) {
        context.addIssue({
            code: "custom",
            message: "A version 2 questionnaire never asked for identity",
            path: ["administrative", "answers", "identity"],
        });
    }
    if (value.clinical === undefined) {
        value.applications.forEach((application, index) => {
            if (SCHEDULING_INTAKE_APPLICATION_SECTION[application.kind] === "clinical") {
                context.addIssue({
                    code: "custom",
                    message: "Health applications are only shown with the health answers",
                    path: ["applications", index, "kind"],
                });
            }
        });
    }
});
/**
 * The staff intake review (`GET /v1/scheduling/appointments/:appointmentId/intake`). Each read
 * is audited, so a client fetches it when the review opens, not on every schedule poll.
 */
export const SchedulingIntakeReviewSchema = z
    .object({
    /** The clinic's time zone: show every time here on the clinic's clock. */
    timeZone: DemoPublicSchedulingConfigurationSchema.shape.timeZone,
    submission: SchedulingIntakeSubmissionSchema.nullable(),
    /** The appointment's consent decisions, one per document kind. */
    consents: z
        .array(z
        .object({
        kind: PatientConsentKindSchema,
        decision: PatientConsentDecisionSchema,
        documentVersion: ConsentDocumentVersionNumberSchema,
        documentTitle: ConsentDocumentTitleSchema,
        channel: PatientConsentChannelSchema,
        recordedAt: IsoDateTimeSchema,
    })
        .strict())
        .max(2)
        .refine((consents) => new Set(consents.map((consent) => consent.kind)).size === consents.length, "One decision per document kind"),
    /**
     * Whether the clinic may text or email about this visit now (PA-8, owner decision O1): the
     * latest decision in the booking's scope, a staff "Patient agreed" or "Patient said no" or a
     * chart-wide decision included, so the review never shows a refusal staff since replaced.
     * consents above stays the questionnaire's own record.
     */
    messagingConsent: PatientMessagingConsentSchema,
    /**
     * The appointment's patient messages (PA-7), in the patient-messaging vocabulary; the
     * booking's messages panel (AppointmentPatientMessagesSchema) has the detail. id is the list
     * key: one booking plans several reminders per channel in one transaction (PA-8).
     */
    messages: z
        .array(z
        .object({
        id: z.string().uuid(),
        kind: PatientMessageKindSchema.exclude(["document_ready"]),
        channel: PatientMessageChannelSchema,
        status: PatientMessageStatusSchema,
        createdAt: IsoDateTimeSchema,
        /** When it is (or was) due; quiet hours may have moved it. */
        scheduledFor: IsoDateTimeSchema,
    })
        .strict())
        .max(1000)
        .refine((messages) => new Set(messages.map((message) => message.id)).size === messages.length, "Each message is listed once"),
    /**
     * What staff confirm about the submitted health card (healthCardFollowUps):
     * computed by the agent-api from the stored coverage answer, never stored.
     */
    healthCardFollowUps: z.array(HealthCardFollowUpSchema).max(3).default([]),
})
    .strict();
/**
 * `POST /v1/scheduling/appointments` on the durable schedule: the prototype's booking request
 * plus, from PA-8, the patient's messaging decision staff heard while booking (owner decision
 * O5). It is recorded in the booking's own transaction, so the confirmation that booking plans is
 * sent (or withheld) on it. The prototype's schema stays without it and refuses the key.
 *
 * A decision that can no longer be taken never loses the booking (lead decision K1): an
 * agreement refused with messaging_not_offered or messaging_notice_changed refuses the whole
 * request, and the client books again at once without messagingDecision (a new idempotency key),
 * then tells staff on the booking's row that the agreement was not recorded, to record it from
 * the booking's messages after reading the current notice.
 */
export const CreateSchedulingAppointmentRequestSchema = CreateDemoAppointmentRequestSchema.extend({
    messagingDecision: StaffMessagingDecisionInputSchema.optional(),
});
export const UpdateSchedulingAppointmentRequestSchema = UpdateDemoAppointmentRequestSchema.omit({
    expectedUpdatedAt: true,
}).extend({ expectedVersion: z.number().int().positive() });
/**
 * `POST /v1/scheduling/appointments/:appointmentId/status`: staff move a booking's status.
 * no_show (PA-9, lead decision L7): from booked or confirmed once the visit has started, after
 * Schedule's confirmation; it cannot be undone. It ends only this booking (its patient links stop
 * working and its time frees up); the patient's other bookings are untouched. No follow-up
 * message is sent (deferred). A staff status change clears the booking's patientChange.
 */
export const TransitionSchedulingAppointmentRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    status: DemoAppointmentStatusSchema,
})
    .strict();
/**
 * `POST /v1/scheduling/appointments/:appointmentId/tokens`: issue a booking's patient links
 * again. The new links replace the ones issued before, which stop working.
 *
 * patientDetails ("Ask for patient details", PA-10 / EXECDEV-217) is for a returning patient's
 * booking that has no chart yet, before arrival, when no matched chart can be linked: the
 * questionnaire link it returns asks every section, About you and coverage included, so the
 * intake review can link or create the chart (ReconcileSchedulingPatientRequestSchema).
 * intakeStatus stays not_required until the answers arrive (completed), since only new patients
 * have pending intake. The server refuses it:
 *
 * - while charts matched at booking await a decision (schedulingPatientMatchUnresolved):
 *   patient_match_review_required;
 * - once the patient's questionnaire for the booking was submitted: intake_already_completed
 *   (review those answers instead);
 * - once the booking has a chart (appointment_changed), the patient has arrived, or the visit
 *   has closed;
 * - while the patient lane is off: patient_links_disabled;
 * - while the patient has the booking's questionnaire link open (the schedule read's
 *   openedDetailsLink) and replaceOpenedLinkId does not name it: intake_link_opened. As with a
 *   visit's link (PA-5, lead decision H7), a patient part-way through their answers is never cut
 *   off silently: the desk confirms first, then names the link it replaces. An unopened link is
 *   simply replaced.
 *
 * replaceOpenedLinkId goes only with patientDetails. agent-api passes patientDetails and
 * replaceOpenedLinkId to the store only when they are set, so a plain re-issue works against a
 * store from before PA-10.
 */
export const IssuePublicBookingTokensRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    patientDetails: z.literal(true).optional(),
    replaceOpenedLinkId: z.string().uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.replaceOpenedLinkId !== undefined && value.patientDetails !== true) {
        context.addIssue({
            code: "custom",
            message: "Only asking for patient details replaces an opened questionnaire link",
            path: ["replaceOpenedLinkId"],
        });
    }
});
/**
 * The re-issued links, returned once. The questionnaire link is present when the booking's
 * questionnaire is pending (required or sent) or patientDetails asked for it.
 */
export const IssuePublicBookingTokensResponseSchema = z
    .object({
    intakeQuestionnaireUrl: PublicIntakeQuestionnaireUrlSchema.optional(),
    manageAppointmentUrl: PublicManageAppointmentUrlSchema,
})
    .strict();
/**
 * `POST /v1/scheduling/appointments/:appointmentId/contact` (owner decision O4, PA-7): correct
 * the booking's phone or email, the contact its texts and emails go to, even once the booking is
 * linked to a chart (the chart is not changed). Name at least one; the phone stays required and
 * an empty email removes it. A value equal to the current one changes nothing. Allowed until the
 * visit ends (booked, confirmed, checked in or in progress; otherwise visit_closed) and audited
 * as scheduling.booking_contact_corrected naming the fields, never the values. Messages still
 * queued go to the corrected contact, since the recipient is read when a message is sent; staff
 * then resend one that failed. Returns the appointment (SchedulingAppointmentSchema).
 */
export const CorrectSchedulingBookingContactRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    phone: DemoAppointmentPatientSchema.shape.phone.optional(),
    email: DemoAppointmentPatientSchema.shape.email.optional(),
})
    .strict()
    .refine((value) => value.phone !== undefined || value.email !== undefined, {
    message: "Correct the phone, the email, or both",
});
/**
 * What the signed-in staff member may change on this site's schedule, from the store's single
 * permission seam. The UI hides what the server would refuse; the server still decides.
 */
export const SchedulingViewerSchema = z
    .object({
    /** The viewer's own physician row on this schedule, if they have one. */
    providerId: ProviderIdSchema.nullable(),
    /** Clinic settings, the physician roster and online booking. */
    canManageSettings: z.boolean(),
    /** Clinic closures and holds on the whole clinic (blocks without a physician). */
    canBlockClinic: z.boolean(),
    /** Physicians whose weekly hours, date overrides and calendar leave the viewer edits. */
    calendarProviderIds: z.array(ProviderIdSchema).max(SCHEDULING_LIMITS.providers),
    /** Physicians whose time the viewer may block on Schedule (leave, unavailable, holds). */
    blockProviderIds: z.array(ProviderIdSchema).max(SCHEDULING_LIMITS.providers),
})
    .strict();
/** A desk-issued link lasts at least this long, even when issued near the clinic's midnight. */
export const VISIT_INTAKE_LINK_MINIMUM_MINUTES = 60;
/**
 * A questionnaire link the desk issued for a visit already in the clinic (PA-5): a walk-in, or
 * a checked-in booking whose questionnaire is not complete (lead decision H5). Listed only while
 * it can still be used: not used, not revoked, and not past expiresAt when read. Never the token
 * or its URL, which only the issue response carries, once.
 */
export const VisitIntakeLinkSchema = z
    .object({
    /** Which link this is, so a desk still showing an older link's code knows it was replaced. */
    id: z.string().uuid(),
    issuedAt: IsoDateTimeSchema,
    /**
     * The end of the clinic's local day of issue, or VISIT_INTAKE_LINK_MINIMUM_MINUTES after
     * issue when that is later. Ending the visit (completed or cancelled) revokes it sooner.
     */
    expiresAt: IsoDateTimeSchema,
    /**
     * The patient opened it with the right date of birth. A new link replaces it only once the
     * desk confirms (IssueVisitIntakeLinkRequest.replaceOpenedLinkId), so a patient part-way
     * through the questionnaire is never cut off silently.
     */
    opened: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    if (Date.parse(value.expiresAt) - Date.parse(value.issuedAt) <
        VISIT_INTAKE_LINK_MINIMUM_MINUTES * 60_000) {
        context.addIssue({
            code: "custom",
            message: `A link lasts at least ${VISIT_INTAKE_LINK_MINIMUM_MINUTES} minutes`,
            path: ["expiresAt"],
        });
    }
});
/** Whether a listed link still works now: the read lists only live links, but time moves on. */
export function isVisitIntakeLinkLive(link, now) {
    return link !== undefined && Date.parse(link.expiresAt) > now.getTime();
}
/** Visit statuses a desk-issued link works in: the patient has arrived and the visit is open. */
const OPEN_VISIT_STATUSES = new Set(["checked_in", "in_progress"]);
/** A live link belongs to an open visit whose questionnaire is not in yet. */
function refineVisitIntakeLink(value, context) {
    if (value.intakeLink !== undefined &&
        (!OPEN_VISIT_STATUSES.has(value.status) || value.intakeStatus === "completed")) {
        context.addIssue({
            code: "custom",
            message: "Only an open visit still waiting for its questionnaire has a live link",
            path: ["intakeLink"],
        });
    }
}
/**
 * The questionnaire link "Ask for patient details" gave a returning patient's booking (PA-10),
 * once the patient opened it with the right date of birth: they may be answering now. Listed
 * while it can still be used and the answers are not in. Never the token or its URL.
 */
export const OpenedBookingIntakeLinkSchema = z
    .object({
    /** Which link this is: the one a confirmed replacement names (replaceOpenedLinkId). */
    id: z.string().uuid(),
    issuedAt: IsoDateTimeSchema,
    /** When the patient first opened it. */
    openedAt: IsoDateTimeSchema,
})
    .strict();
export const SchedulingAppointmentSchema = DemoAppointmentSchema.safeExtend({
    version: z.number().int().positive(),
    /**
     * A checked-in booking's desk-issued questionnaire link while it is live (PA-5). Absent
     * otherwise, including links sent before arrival, which check-in revokes. Check-in needs the
     * questionnaire done or not required, and only new patients may have one pending, so issuing
     * leaves intakeStatus as it was (not_required) until the answers arrive (completed).
     */
    intakeLink: VisitIntakeLinkSchema.optional(),
    /**
     * PA-10: a returning patient's booking without a chart, before arrival and before any answers,
     * whose questionnaire link from "Ask for patient details" the patient opened. Review booking
     * then says they may be answering, and asks before a new code replaces it (lead decision H7).
     * Absent otherwise, including while the link is unopened (before arrival only that request
     * gives a returning patient a questionnaire link), and on every payload from before PA-10.
     */
    openedDetailsLink: OpenedBookingIntakeLinkSchema.optional(),
}).superRefine((value, context) => {
    refineVisitIntakeLink(value, context);
    if (value.openedDetailsLink !== undefined &&
        (value.patient.kind !== "returning" ||
            value.patient.patientId !== undefined ||
            !["booked", "confirmed"].includes(value.status) ||
            value.intakeStatus === "completed" ||
            value.intakeReview !== undefined)) {
        context.addIssue({
            code: "custom",
            message: "Only a returning patient's booking without a chart or answers, before arrival, has an opened details link",
            path: ["openedDetailsLink"],
        });
    }
});
/** At most this many walk-ins on the schedule read. */
export const SCHEDULING_WALK_IN_LIMIT = 500;
/**
 * A walk-in visit's scheduling row (source walk_in, PA-5): created when the desk first sends the
 * walk-in a questionnaire link and reused for every later one. It carries the link, the
 * questionnaire and its review the way a booking does, and never a physician, a visit type, a
 * calendar time or any patient detail: Today's board already has the patient, and finds the row
 * by encounterId. Never in `appointments`, availability, closures or settings checks.
 */
export const SchedulingWalkInSchema = z
    .object({
    /** The scheduling row id: the id the intake review routes take (PA-6). */
    id: z.string().uuid(),
    version: z.number().int().positive(),
    encounterId: z.string().uuid(),
    /** Always set: a walk-in is on a chart before it can get a link. */
    patientId: z.string().uuid(),
    /** When the visit arrived (the row's start). */
    arrivedAt: IsoDateTimeSchema,
    /** Follows the visit; clinical "left" is cancelled. */
    status: DemoAppointmentStatusSchema.extract([
        "checked_in",
        "in_progress",
        "completed",
        "cancelled",
    ]),
    /** sent until the answers arrive (with or without a live link), then completed. */
    intakeStatus: DemoScheduledIntakeStatusSchema.extract(["sent", "completed"]),
    /** What the latest link asks: About you only when this visit created the chart. */
    questionnaireSections: PatientIntakeSectionsSchema,
    /** The live link; absent once used, revoked or expired ("Code expired" while still sent). */
    intakeLink: VisitIntakeLinkSchema.optional(),
    /** As on appointments: present once answers were submitted, sections by the viewer's role. */
    intakeReview: SchedulingIntakeReviewSummarySchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    refineVisitIntakeLink(value, context);
    if (value.intakeReview !== undefined && value.intakeStatus !== "completed") {
        context.addIssue({
            code: "custom",
            message: "Only submitted answers have a review",
            path: ["intakeReview"],
        });
    }
});
/** The durable schedule read: versioned appointments and what the viewer may change. */
export const SchedulingStateSchema = DemoSchedulingStateSchema.safeExtend({
    appointments: z.array(SchedulingAppointmentSchema),
    viewer: SchedulingViewerSchema,
    /**
     * Walk-ins with a desk-issued questionnaire link (PA-5): those still open, those that arrived
     * on the clinic's current local day, and those completed that day, whose review is still open
     * (a visit across midnight keeps its chart in the review), newest first. The store leaves the
     * key out when there are none, so the read is unchanged until a first link is issued.
     */
    walkIns: z
        .array(SchedulingWalkInSchema)
        .max(SCHEDULING_WALK_IN_LIMIT)
        .refine((walkIns) => new Set(walkIns.map((walkIn) => walkIn.encounterId)).size === walkIns.length, "Each visit has one walk-in row")
        .default([]),
    /**
     * Whether the desk can issue questionnaire links: the public patient lane is configured.
     * agent-api sets it, not the store. While false the UI offers no code and the issue route
     * refuses (patient_links_disabled).
     */
    patientLinksEnabled: z.boolean().default(false),
});
/** Opaque sha256 hex the server derives from exactly what a save would affect. */
export const SchedulingAcknowledgementTokenSchema = z.string().regex(/^[0-9a-f]{64}$/);
/**
 * What a settings or calendar save would affect, returned instead of saving. Bookings are never
 * moved or cancelled. Re-sending the save with acknowledgementToken confirms exactly this impact;
 * if what is affected changes in between, the server asks again.
 */
export const SchedulingSaveImpactSchema = z
    .object({
    acknowledgementToken: SchedulingAcknowledgementTokenSchema,
    /** Future booked or confirmed appointments the change newly leaves outside the schedule. */
    strandedAppointmentCount: z.number().int().nonnegative(),
    /** The earliest of them by start time. */
    strandedAppointmentIds: z
        .array(z.string().uuid())
        .max(SCHEDULING_LIMITS.acknowledgementAppointmentIds),
    /**
     * The booking link changes: intake and manage links already sent stop working, and website
     * snippets with the old link must be updated.
     */
    bookingLinkChange: z
        .object({ liveLinkCount: z.number().int().nonnegative() })
        .strict()
        .optional(),
    /** Walk-in only hides Schedule while these future bookings remain. */
    walkInOnly: z.object({ futureBookingCount: z.number().int().positive() }).strict().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const listed = Math.min(value.strandedAppointmentCount, SCHEDULING_LIMITS.acknowledgementAppointmentIds);
    if (value.strandedAppointmentIds.length !== listed) {
        context.addIssue({
            code: "custom",
            message: "Stranded appointments must be listed up to the limit",
            path: ["strandedAppointmentIds"],
        });
    }
    if (value.strandedAppointmentCount === 0 &&
        value.bookingLinkChange === undefined &&
        value.walkInOnly === undefined) {
        context.addIssue({ code: "custom", message: "An acknowledgement must name an impact" });
    }
});
export const SchedulingSaveResultSchema = z.discriminatedUnion("status", [
    z
        .object({ status: z.literal("saved"), configuration: DemoSchedulingConfigurationSchema })
        .strict(),
    z
        .object({ status: z.literal("acknowledgement_required"), impact: SchedulingSaveImpactSchema })
        .strict(),
]);
/** A clinic settings save. Physician calendars are saved on their own. */
export const UpdateSchedulingSettingsRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    configuration: SchedulingSettingsSchema,
    acknowledgementToken: SchedulingAcknowledgementTokenSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.configuration.version !== value.expectedVersion) {
        context.addIssue({
            code: "custom",
            message: "Configuration version must match the expected version",
            path: ["configuration", "version"],
        });
    }
});
/** One physician's calendar save, against that physician's availabilityVersion. */
export const SaveProviderAvailabilityRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    availability: ProviderAvailabilitySchema,
    acknowledgementToken: SchedulingAcknowledgementTokenSchema.optional(),
})
    .strict();
/**
 * PHI-free tokens the staff scheduling API puts in an error's detail, so the UI can react (reload,
 * offer deactivation) without reading messages.
 */
export const SchedulingErrorDetailSchema = z.enum([
    /** Someone saved the clinic settings first. */
    "scheduling_settings_changed",
    /** Someone saved this physician's calendar first. */
    "provider_availability_changed",
    /** The appointment changed since it was read. */
    "appointment_changed",
    /** Another clinic uses that booking link. */
    "booking_link_taken",
    /** The visit type is still on a physician's calendar. */
    "visit_type_in_calendars",
    /** The physician has appointments or blocks: deactivate instead of removing. */
    "provider_has_history",
    /** Blocked time overlaps a booked appointment. */
    "block_overlaps_appointment",
    /** Accepting online bookings needs a physician account linked to the provider. */
    "physician_link_required",
    /** The save would pass a SCHEDULING_LIMITS total. */
    "scheduling_limit_reached",
    /** Someone published a newer version of this patient terms document first. */
    "consent_document_changed",
    /** The intake review has closed: answers can be read but no longer applied or reviewed. */
    "intake_review_closed",
    /** The probable charts changed since they were read: reload them and decide again. */
    "chart_matches_changed",
    /** The patient's chart changed since it was read: reload it before applying. */
    "chart_changed",
    /** The questionnaire did not ask for identity, so no chart or identity comes from it. */
    "intake_identity_missing",
    /** The patient's reason is longer than a booking reason: edit the booking instead. */
    "intake_reason_too_long",
    /**
     * The visit has ended (completed, cancelled or left), so it cannot get a questionnaire link,
     * nor (PA-7) its booking contact be corrected.
     */
    "visit_closed",
    /**
     * The patient already sent this visit's (or, for "Ask for patient details", this booking's)
     * questionnaire: review it rather than send a link.
     */
    "intake_already_completed",
    /** The public patient lane is off, so no questionnaire link can be issued. */
    "patient_links_disabled",
    /**
     * The patient opened the visit's live link (or, PA-10, the booking's questionnaire link from
     * "Ask for patient details") and may be answering it: the desk confirms replacing that link
     * (naming it) before a new one is issued.
     */
    "intake_link_opened",
    /**
     * Charts matched when the patient booked still await a decision (likely, several or
     * card_other_dob): link one or rule them out before asking the patient for their details
     * (PA-10).
     */
    "patient_match_review_required",
    /** Check-in needs the booking linked to a chart: link or create it first (PA-10). */
    "patient_chart_required",
    /**
     * PA-7 Resend: a message of this kind and channel is still queued, being sent, or may have been
     * sent (uncertain), so another would risk sending twice. Wait for it to settle.
     */
    "message_in_flight",
    /** PA-7 Resend: this message cannot be resent (the booking is no longer booked or confirmed). */
    "message_not_resendable",
    /**
     * PA-7: the message changed since it was read (resent, marked done, or the booking moved on):
     * reload the booking's messages.
     */
    "message_changed",
    /** PA-7 Resend: the patient has not agreed to texts and emails, or said no. */
    "message_consent_missing",
    /** PA-7 Resend: the number replied STOP to this clinic; only the patient can restart texts. */
    "message_recipient_stopped",
    /** PA-7 Resend: the booking has no phone (texts) or email (emails): correct the contact. */
    "message_recipient_missing",
    /**
     * PA-7 Resend: the booking's phone cannot take a text (not a North American number) or its
     * email is not an address: correct the contact.
     */
    "message_recipient_invalid",
    /**
     * PA-7: the clinic does not send this kind of message on this channel. PA-8: staff cannot
     * record "Patient agreed" while the clinic offers no channel (a refusal is always taken).
     */
    "messaging_not_offered",
    /** PA-7 Resend: this booking's messages were resent the most times allowed. */
    "message_resend_limit",
    /** PA-8: someone saved the clinic's messaging settings first: reload and compare. */
    "messaging_settings_changed",
    /** PA-8: someone published a newer version of this message's wording first. */
    "message_template_changed",
    /** PA-8: the channel has no sender set up (the operator's step), so it can't be turned on or tested. */
    "messaging_sender_missing",
    /**
     * PA-8: the clinic published a newer electronic messaging notice since it was read to the
     * patient: read the new one, then record the agreement.
     */
    "messaging_notice_changed",
    /** PA-8: this staff member sent the most test messages allowed in an hour. */
    "message_test_limit_reached",
    /**
     * PA-13: the clinic is not taking health card photos now (no photo checker is running): hide
     * Take / Add photo and type the details.
     */
    "health_card_photos_unavailable",
    "document_delivery_off",
    "document_not_deliverable",
    /** PA-13: the most health card photos allowed were already sent. */
    "health_card_photo_limit",
    /**
     * PA-13: the photo is not a plain JPEG within the limits (inspectHealthCardJpeg): take it
     * again.
     */
    "health_card_photo_invalid",
    /** PA-13: the photo is still being checked for safety, or the check blocked it. */
    "health_card_photo_not_ready",
    /** PA-13: someone saved the clinic's health card photo settings first: reload them. */
    "health_card_photo_settings_changed",
]);
//# sourceMappingURL=scheduling.js.map